<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { toast } from 'dashboard/helper/toast';
import { useAutomation } from 'dashboard/composables/useAutomation';
import PageLayout from 'dashboard/components-next/layout/PageLayout.vue';
import PageHeader from 'dashboard/components-next/layout/PageHeader.vue';
import AutomationForm from 'dashboard/components-next/automation/AutomationForm.vue';

const START_VALUE = {
  name: null,
  description: null,
  event_name: 'conversation_created',
  conditions: [
    {
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    },
  ],
  actions: [
    {
      action_name: 'assign_agent',
      action_params: [],
    },
  ],
};

const { t } = useI18n();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();

const accountId = computed(() => getters.getCurrentAccountId.value);

const {
  automation,
  automationTypes,
  onEventChange,
  getConditionDropdownValues,
  appendNewAction,
  removeAction,
  resetAction,
  getActionDropdownValues,
  manifestCustomAttributes,
} = useAutomation(structuredClone(START_VALUE));

const allCustomAttributes = ref([]);
const isSubmitting = ref(false);

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('agents/get');
  store.dispatch('contacts/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  store.dispatch('campaigns/get');
  allCustomAttributes.value = getters['attributes/getAttributes'].value;
  manifestCustomAttributes();
});

// Botão Voltar/Cancelar context-aware.
const goBack = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push({
      name: 'automation_list',
      params: { accountId: accountId.value },
    });
  }
};

const submitAutomation = async payload => {
  isSubmitting.value = true;
  try {
    await store.dispatch('automations/create', payload);
    toast.success(t('AUTOMATION.ADD.API.SUCCESS_MESSAGE'));
    router.push({
      name: 'automation_list',
      params: { accountId: accountId.value },
    });
  } catch (error) {
    // 422 traz o motivo da recusa (ex.: ação de destino que falta); sem ele
    // o usuário só via "tente novamente mais tarde".
    toast.error(
      error?.response?.data?.message || t('AUTOMATION.ADD.API.ERROR_MESSAGE')
    );
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <PageLayout body-padding="">
    <template #header>
      <PageHeader
        icon="i-lucide-workflow"
        accent="iris"
        :title="t('AUTOMATION.ADD.TITLE')"
        :subtitle="t('AUTOMATION.DESCRIPTION')"
        back-route="automation_list"
      />
    </template>
    <AutomationForm
      v-model:automation="automation"
      :automation-types="automationTypes"
      mode="create"
      :is-submitting="isSubmitting"
      :all-custom-attributes="allCustomAttributes"
      :on-event-change="onEventChange"
      :get-condition-dropdown-values="getConditionDropdownValues"
      :get-action-dropdown-values="getActionDropdownValues"
      :append-new-action="appendNewAction"
      :remove-action="removeAction"
      :reset-action="resetAction"
      @submit="submitAutomation"
      @cancel="goBack"
    />
  </PageLayout>
</template>
